import { useState } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { UserOrganisation } from '../../types/types';
import { supabase } from '../../lib/supabase';
import { captureSupabaseError } from '../../lib/sentry';

interface NewQuestionnaireModalProps {
  show: boolean;
  onHide: () => void;
}

export default function NewQuestionnaireModal({
  show,
  onHide,
}: NewQuestionnaireModalProps) {
  const navigate = useNavigate();

  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected
  ) as UserOrganisation | undefined;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOrganisation?.organisation?.id) {
      setError('No organisation selected.');
      return;
    }

    if (!title.trim()) {
      setError('Title is required.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('questionnaire_templates')
        .insert([
          {
            title,
            description,
            status,
            tags: tags
              ? tags
                  .split(',')
                  .map((t) => t.trim())
                  .filter(Boolean)
              : [],
            organisation_id: selectedOrganisation.organisation.id,
          },
        ])
        .select('id')
        .single();

      if (error) throw error;

      // Redirect to builder page
      onHide();
      navigate(`/questionnaires/${data.id}/edit`);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      captureSupabaseError(err, { op: 'questionnaire.create' });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setTags('');
    setStatus('draft');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Form onSubmit={handleCreate}>
        <Modal.Header closeButton>
          <Modal.Title>New Questionnaire</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form.Group className="mb-3" controlId="questionnaireTitle">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter questionnaire title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="questionnaireDescription">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Describe what this questionnaire is for..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="questionnaireTags">
            <Form.Label>Tags</Form.Label>
            <Form.Control
              type="text"
              placeholder="Comma-separated tags (e.g. security, compliance)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="questionnaireStatus">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as 'draft' | 'published')
              }
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Creating...
              </>
            ) : (
              'Create Questionnaire'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
